import type { TelemetryEvent, UserContextEvent } from './telemetry-models';
import { TelemetryEventType } from './telemetry-models';
import { BaseTracker } from './base-tracker';
import { DeviceDetector } from './detectors/device-detector';
import { DisplayDetector } from './detectors/display-detector';
import { NetworkDetector } from './detectors/network-detector';
import { CapabilitiesDetector } from './detectors/capabilities-detector';
import { PreferencesDetector } from './detectors/preferences-detector';
import { OriginsDetector } from './detectors/origins-detector';

const USER_CONTEXT_SENT_KEY = '__telemetry_user_context_sent__';

/**
 * Tracks device and environment context, monitoring for significant changes.
 */
export class UserContextTracker extends BaseTracker {
  private _currentContext: UserContextEvent | null = null;
  private _networkListener: (() => void) | null = null;
  private _resizeListener: (() => void) | null = null;
  private _orientationListener: (() => void) | null = null;
  private _resizeTimeout: ReturnType<typeof setTimeout> | null = null;
  private _customContextData?: Record<string, unknown>;
  private _sessionId: string;

  // Thresholds for triggering updates
  private readonly VIEWPORT_CHANGE_THRESHOLD = 100; // pixels
  private readonly NETWORK_RTT_THRESHOLD = 100; // ms
  private readonly NETWORK_DOWNLINK_THRESHOLD = 2; // Mbps

  constructor(
    onEvent: (event: TelemetryEvent) => void,
    sessionId: string,
    enabledLogging = false,
    customContextData?: Record<string, unknown>
  ) {
    super(onEvent, enabledLogging);
    this._sessionId = sessionId;
    this._customContextData = customContextData;
  }

  /** Capture initial context and start monitoring for changes. */
  public async start(): Promise<void> {
    // Check if user-context has already been sent in this session
    if (this._hasUserContextBeenSent()) {
      this._log('User context already sent in this session, skipping initial capture');
      this._setupMonitoring(); // Still monitor for changes
      return;
    }

    // Capture both sync and async data before emitting first event
    await this._captureInitialContext();
    this._setupMonitoring();
    this._log('Context monitoring started');
  }

  public stop(): void {
    this._cleanupMonitoring();
    this._log('Context monitoring stopped');
  }

  /**
   * Check if user-context has already been sent in this session
   */
  private _hasUserContextBeenSent(): boolean {
    try {
      const sent = sessionStorage.getItem(USER_CONTEXT_SENT_KEY);
      const storedSessionId = sessionStorage.getItem('__telemetry_session_id__');

      // Only consider it sent if both flags are true AND the session ID matches
      return sent === 'true' && storedSessionId === this._sessionId;
    } catch {
      // If sessionStorage is not available, allow sending
      return false;
    }
  }

  /**
   * Mark that user-context has been sent in this session
   */
  private _markUserContextAsSent(): void {
    try {
      sessionStorage.setItem(USER_CONTEXT_SENT_KEY, 'true');
    } catch {
      // Ignore errors - not critical
    }
  }

  /**
   * Capture initial context with both sync and async data
   * Only emits one event after all data is collected
   */
  private async _captureInitialContext(): Promise<void> {
    try {
      // Gather synchronous context data
      const syncContext: UserContextEvent = {
        timestamp: Date.now(),
        ...DeviceDetector.detect(),
        ...DisplayDetector.captureDisplay(),
        ...DisplayDetector.captureViewport(),
        ...NetworkDetector.capture(),
        ...CapabilitiesDetector.detect(),
        ...PreferencesDetector.detect(),
        ...OriginsDetector.detect(),
      };

      // Gather async capabilities
      const asyncData = await CapabilitiesDetector.captureAsync();

      // Merge both sync and async data, including custom data with context_ prefix
      const completeContext: UserContextEvent = {
        ...syncContext,
        ...asyncData,
        timestamp: Date.now(), // Update timestamp to reflect completion
        ...this._getPrefixedCustomData(),
      };

      this._currentContext = completeContext;

      // Emit single event with complete data
      this._onEvent({
        type: TelemetryEventType.USER_CONTEXT,
        data: completeContext,
      });

      // Mark as sent to prevent duplicate events in this session
      this._markUserContextAsSent();

      this._log('Initial context captured with async capabilities');
    } catch (error) {
      // Fallback: if async fails, send sync data only
      this._log('WARN: Failed to capture async capabilities, sending sync data only', {
        error: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
      });

      const syncContext: UserContextEvent = {
        timestamp: Date.now(),
        ...DeviceDetector.detect(),
        ...DisplayDetector.captureDisplay(),
        ...DisplayDetector.captureViewport(),
        ...NetworkDetector.capture(),
        ...CapabilitiesDetector.detect(),
        ...PreferencesDetector.detect(),
        ...OriginsDetector.detect(),
        ...this._getPrefixedCustomData(),
      };

      this._currentContext = syncContext;
      this._onEvent({
        type: TelemetryEventType.USER_CONTEXT,
        data: syncContext,
      });

      // Mark as sent even on fallback
      this._markUserContextAsSent();
    }
  }

  /**
   * Capture context updates (for monitoring changes)
   * Used by event handlers after initial capture
   */
  private _captureContextUpdate(): void {
    const context: UserContextEvent = {
      timestamp: Date.now(),
      ...DeviceDetector.detect(),
      ...DisplayDetector.captureDisplay(),
      ...DisplayDetector.captureViewport(),
      ...NetworkDetector.capture(),
      ...CapabilitiesDetector.detect(),
      ...PreferencesDetector.detect(),
      ...OriginsDetector.detect(),
    };

    this._currentContext = context;
    this._onEvent({ type: TelemetryEventType.USER_CONTEXT, data: context });
  }

  /**
   * Get custom context data with context_ prefix
   */
  private _getPrefixedCustomData(): Record<string, unknown> {
    if (!this._customContextData || Object.keys(this._customContextData).length === 0) {
      return {};
    }

    const prefixed: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(this._customContextData)) {
      prefixed[`context_${key}`] = value;
    }
    return prefixed;
  }

  private _setupMonitoring(): void {
    // Monitor network changes
    const conn = NetworkDetector.getConnection();
    if (conn) {
      this._networkListener = () => this._handleNetworkChange();
      conn.addEventListener('change', this._networkListener);
    }

    // Monitor viewport changes (debounced)
    this._resizeListener = () => {
      if (this._resizeTimeout) {
        clearTimeout(this._resizeTimeout);
      }
      this._resizeTimeout = setTimeout(() => {
        this._handleViewportChange();
        this._resizeTimeout = null;
      }, 100);
    };
    window.addEventListener('resize', this._resizeListener);

    // Monitor orientation changes
    if (screen.orientation) {
      this._orientationListener = () => this._handleOrientationChange();
      screen.orientation.addEventListener('change', this._orientationListener);
    }
  }

  private _cleanupMonitoring(): void {
    const conn = NetworkDetector.getConnection();
    if (conn && this._networkListener) {
      conn.removeEventListener('change', this._networkListener);
    }

    if (this._resizeListener) {
      window.removeEventListener('resize', this._resizeListener);
    }

    if (this._resizeTimeout) {
      clearTimeout(this._resizeTimeout);
      this._resizeTimeout = null;
    }

    if (screen.orientation && this._orientationListener) {
      screen.orientation.removeEventListener('change', this._orientationListener);
    }
  }

  private _handleNetworkChange(): void {
    const newNetwork = NetworkDetector.capture();
    const oldNetwork = this._currentContext;

    if (
      oldNetwork &&
      NetworkDetector.isSignificantChange(oldNetwork, newNetwork, {
        rtt: this.NETWORK_RTT_THRESHOLD,
        downlink: this.NETWORK_DOWNLINK_THRESHOLD,
      })
    ) {
      this._captureContextUpdate();
      this._log('Significant network change detected');
    }
  }

  private _handleViewportChange(): void {
    const newViewport = DisplayDetector.captureViewport();
    const oldViewport = this._currentContext;

    if (DisplayDetector.isSignificantViewportChange(oldViewport, newViewport, this.VIEWPORT_CHANGE_THRESHOLD)) {
      this._captureContextUpdate();
      this._log('Significant viewport change detected');
    }
  }

  private _handleOrientationChange(): void {
    this._captureContextUpdate();
    this._log('Orientation changed');
  }
}
